import { ShotVersion } from '../../../types';
import {
  delay as defaultDelay,
  hasSubmittedVersion,
  PendingVideoSubmission,
  VIDEO_SUBMIT_LOCK_MAX_ATTEMPTS,
  VIDEO_SUBMIT_LOCK_RETRY_DELAY_MS,
} from './videoSubmitLock';

export interface VideoSubmitContext {
  shotId: string;
  submissionToken: symbol;
  submissionScope: number;
}

export interface GenerateVideoResult {
  versionId?: string;
}

export interface VideoSubmitFlowDeps {
  episodeId?: string;
  isCurrent: (ctx: VideoSubmitContext) => boolean;
  markPending: (shotId: string, value: PendingVideoSubmission) => void;
  clearPending: (shotId: string) => void;
  generateVideo: (shotId: string) => Promise<GenerateVideoResult>;
  refreshShots: (episodeId: string, ctx: VideoSubmitContext) => Promise<void>;
  listShotVersions: (shotId: string) => Promise<ShotVersion[]>;
  setShotVersions: (shotId: string, versions: ShotVersion[]) => void;
  info: () => void;
  error: (err: unknown) => void;
  log: (message: string, err: unknown) => void;
  delay?: (ms: number) => Promise<void>;
  maxAttempts?: number;
  retryDelayMs?: number;
}

async function waitForSubmittedVersion(
  ctx: VideoSubmitContext,
  versionId: string,
  deps: VideoSubmitFlowDeps
): Promise<boolean> {
  const delay = deps.delay || defaultDelay;
  const maxAttempts = deps.maxAttempts ?? VIDEO_SUBMIT_LOCK_MAX_ATTEMPTS;
  const retryDelayMs = deps.retryDelayMs ?? VIDEO_SUBMIT_LOCK_RETRY_DELAY_MS;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (!deps.isCurrent(ctx)) {
      return false;
    }

    if (attempt > 0) {
      await delay(retryDelayMs);
      if (!deps.isCurrent(ctx)) {
        return false;
      }
    }

    try {
      const versions = await deps.listShotVersions(ctx.shotId);
      if (!deps.isCurrent(ctx)) {
        return false;
      }

      deps.setShotVersions(ctx.shotId, versions);
      if (hasSubmittedVersion(versions, versionId)) {
        return true;
      }
    } catch (err) {
      if (!deps.isCurrent(ctx)) {
        return false;
      }
      deps.log('Failed to load versions while waiting for submitted video:', err);
    }
  }

  if (!deps.isCurrent(ctx)) {
    return false;
  }

  deps.info();
  return false;
}

export async function runVideoSubmitFlow(
  ctx: VideoSubmitContext,
  deps: VideoSubmitFlowDeps
): Promise<void> {
  deps.markPending(ctx.shotId, true);

  try {
    const result = await deps.generateVideo(ctx.shotId);
    if (!deps.isCurrent(ctx)) {
      return;
    }

    if (deps.episodeId) {
      await deps.refreshShots(deps.episodeId, ctx);
      if (!deps.isCurrent(ctx)) {
        return;
      }
    }

    if (!result.versionId) {
      try {
        const versions = await deps.listShotVersions(ctx.shotId);
        if (!deps.isCurrent(ctx)) {
          return;
        }

        deps.setShotVersions(ctx.shotId, versions);
      } catch (err) {
        if (!deps.isCurrent(ctx)) {
          return;
        }
        deps.log('Failed to load versions after submitted video:', err);
      }

      deps.info();
      return;
    }

    deps.markPending(ctx.shotId, result.versionId);
    await waitForSubmittedVersion(ctx, result.versionId, deps);
  } catch (err) {
    if (deps.isCurrent(ctx)) {
      deps.error(err);
    }
  } finally {
    if (deps.isCurrent(ctx)) {
      deps.clearPending(ctx.shotId);
    }
  }
}
